import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import PageShell from './PageShell';
import { Lock, Eye, EyeOff, CheckCircle2, AlertCircle, Loader2, ArrowRight } from 'lucide-react';

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState('loading'); // loading | invalid | form | success
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.verifyResetToken(token).then((d) => {
      setEmail(d.email);
      setState('form');
    }).catch((e) => {
      setErr(e?.response?.data?.detail || 'Lien invalide');
      setState('invalid');
    });
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pwd.length < 8) { setErr('8 caractères minimum'); return; }
    if (pwd !== pwd2) { setErr('Les deux mots de passe ne correspondent pas'); return; }
    setSubmitting(true);
    try {
      await api.resetPassword({ token, new_password: pwd });
      setState('success');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Erreur. Réessayez.');
    } finally { setSubmitting(false); }
  };

  return (
    <PageShell>
      <section className="max-w-md mx-auto px-4 lg:px-6 mt-16">
        <div className="rounded-3xl border border-white/10 bg-[#0d0d0e] p-8 lg:p-10">
          {state === 'loading' && (
            <div className="text-center text-white/65 py-6">
              <Loader2 className="animate-spin mx-auto mb-3" /> Vérification du lien…
            </div>
          )}

          {state === 'invalid' && (
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 mx-auto mb-4"><AlertCircle size={22} /></div>
              <h1 className="font-serif-editorial text-white text-[26px] leading-tight">Lien invalide ou expiré</h1>
              <p className="text-white/60 text-[14px] mt-3">{err}</p>
              <button data-testid="reset-back-home" onClick={() => navigate('/')} className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold">
                Retour à l'accueil <ArrowRight size={14} />
              </button>
            </div>
          )}

          {state === 'form' && (
            <>
              <div className="text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2">Nouveau mot de passe</div>
              <h1 className="font-serif-editorial text-white text-[28px] leading-tight">Choisis ton nouveau mot de passe</h1>
              <p className="text-white/55 text-[13.5px] mt-2">Pour <strong className="text-white">{email}</strong></p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Nouveau mot de passe</label>
                  <div className="mt-1 relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="reset-pwd-input"
                      required minLength={8}
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      type={show ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Min. 8 caractères"
                      className="w-full h-11 pl-9 pr-11 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition"
                    />
                    <button type="button" data-testid="reset-toggle-pwd" tabIndex={-1} onClick={() => setShow((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-md flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.05]">
                      {show ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-white/50 font-semibold">Confirmer</label>
                  <div className="mt-1 relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      data-testid="reset-pwd2-input"
                      required minLength={8}
                      value={pwd2}
                      onChange={(e) => setPwd2(e.target.value)}
                      type={show ? 'text' : 'password'}
                      autoComplete="new-password"
                      placeholder="Retape le même"
                      className="w-full h-11 pl-9 pr-3 rounded-lg bg-white/5 border border-white/15 text-white text-[14px] outline-none focus:border-red-500 transition"
                    />
                  </div>
                </div>

                {err && <div data-testid="reset-error" className="text-red-400 text-[12.5px] bg-red-950/30 border border-red-900/40 rounded-lg px-3 py-2">{err}</div>}

                <button data-testid="reset-submit" type="submit" disabled={submitting} className="w-full h-12 rounded-full bg-red-600 hover:bg-red-500 text-white text-[14px] font-semibold inline-flex items-center justify-center gap-2 shadow-lg shadow-red-900/40 disabled:opacity-50">
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <>Mettre à jour mon mot de passe <ArrowRight size={15} /></>}
                </button>
              </form>
            </>
          )}

          {state === 'success' && (
            <div data-testid="reset-success" className="text-center">
              <div className="w-14 h-14 rounded-full bg-green-600/15 border border-green-700/30 flex items-center justify-center text-green-400 mx-auto mb-4"><CheckCircle2 size={22} /></div>
              <h1 className="font-serif-editorial text-white text-[26px] leading-tight">Mot de passe mis à jour</h1>
              <p className="text-white/60 text-[14px] mt-3">Tu peux maintenant te connecter avec ton nouveau mot de passe.</p>
              <button data-testid="reset-to-login" onClick={() => navigate('/')} className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-red-600 hover:bg-red-500 text-white text-[13.5px] font-semibold">
                Aller à la connexion <ArrowRight size={14} />
              </button>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
}
